/**
 * Input replay schema. The ghost re-runs the lap by feeding the player's
 * recorded (throttle, brake, steer) into the same physics step the live
 * racer uses. Banking, hover bob, and acceleration squat all emerge from
 * physics — there's no keyframed visual state.
 */

import { FIXED_UPDATE_DT } from '../game/FrameTiming';

export interface GhostStartPose {
  x: number;
  z: number;
  heading: number;
}

export interface GhostStartState extends GhostStartPose {
  y: number;
  velocityX: number;
  velocityZ: number;
}

export type GhostStateSample = GhostStartState;

/** Parallel arrays for tight JSON. One entry per fixed physics step. */
export interface GhostInputTrack {
  throttle: number[];
  brake: number[];
  steer: number[];
  /** Whether the racer was on the racing surface that step. Replays it
   *  through the same drag/grip branch the player took. */
  onTrack: boolean[];
}

export interface GhostRecording {
  /**
   * v3: states carry x, z, heading, velocityX, velocityZ — height is re-derived
   *     at playback by sampling the live ground (legacy, kept playable).
   * v4: states additionally carry y, so the ghost rides the elevation the
   *     player actually had even if terrain is retuned later.
   */
  schemaVersion: 3 | 4;
  trackId: string;
  /** Total lap time in ms — the lap the recording was set on. */
  lapMs: number;
  /** Physics version that produced this recording. Older versions are
   *  rejected by loadGhost so we never play a stale ghost on new physics.
   *  Recordings are only ever saved from standard race mode (assisted modes
   *  are filtered out before finalize), so there's no raceMode field — the
   *  ghost always replays under standard physics. */
  physicsVersion: string;
  /** Step duration in seconds (typically the universal fixed-step dt). */
  stepDt: number;
  /** Full physics state at the timing gate for the recorded lap. */
  startState: GhostStartState;
  inputs: GhostInputTrack;
  /**
   * Post-step racer states after all live-game correction has run. These are
   * authoritative for visual replay; inputs remain as telemetry/debug data.
   */
  states: GhostStateSample[];
}

const MAX_GHOST_RECORDING_SECONDS = 5 * 60;

export class GhostRecorder {
  private currentLapStartState: GhostStartState | null = null;
  private throttle: number[] = [];
  private brake: number[] = [];
  private steer: number[] = [];
  private onTrack: boolean[] = [];
  private states: GhostStateSample[] = [];
  private active = false;
  private stepDt = FIXED_UPDATE_DT;

  /**
   * Begin buffering inputs for a time-trial session. Inputs before the first
   * gate crossing are only the player's rolling start and are discarded when
   * {@link markLapStart} arms the lap recording.
   */
  start(stepDt: number): void {
    this.stepDt = stepDt;
    this.throttle = [];
    this.brake = [];
    this.steer = [];
    this.onTrack = [];
    this.states = [];
    this.currentLapStartState = null;
    this.active = true;
  }

  reset(): void {
    this.currentLapStartState = null;
    this.throttle = [];
    this.brake = [];
    this.steer = [];
    this.onTrack = [];
    this.states = [];
    this.active = false;
  }

  isActive(): boolean {
    return this.active;
  }

  stepCount(): number {
    return this.throttle.length;
  }

  recordStep(
    throttle: number,
    brake: number,
    steer: number,
    onTrack: boolean,
    state?: GhostStateSample,
  ): void {
    if (!this.active) return;
    if (!this.currentLapStartState) return;
    if (!state) return;
    if (this.throttle.length >= maxGhostRecordingSteps(this.stepDt)) return;
    this.throttle.push(throttle);
    this.brake.push(brake);
    this.steer.push(steer);
    this.onTrack.push(onTrack);
    this.states.push({ ...state });
  }

  /** Mark the current physics state as a lap start (gate crossing). Called
   *  for the first gate cross of the session and every subsequent one. */
  markLapStart(state: GhostStartState): void {
    if (!this.active) return;
    this.currentLapStartState = { ...state };
    this.throttle = [];
    this.brake = [];
    this.steer = [];
    this.onTrack = [];
    this.states = [];
  }

  /**
   * Build a recording for the currently active lap. The replay starts from
   * the saved timing-gate physics state, so later-lap PBs keep the exact
   * velocity and position they had when that lap began.
   */
  finalize(
    trackId: string,
    lapMs: number,
    physicsVersion: string,
  ): GhostRecording | null {
    if (!this.currentLapStartState) return null;
    if (this.throttle.length < 2) return null;
    if (this.states.length !== this.throttle.length) return null;

    return {
      schemaVersion: 4,
      trackId,
      lapMs,
      physicsVersion,
      stepDt: this.stepDt,
      startState: { ...this.currentLapStartState },
      inputs: {
        throttle: this.throttle.slice(),
        brake: this.brake.slice(),
        steer: this.steer.slice(),
        onTrack: this.onTrack.slice(),
      },
      states: this.states.slice(),
    };
  }
}

function maxGhostRecordingSteps(stepDt: number): number {
  const safeStepDt = Number.isFinite(stepDt) && stepDt > 0 ? stepDt : FIXED_UPDATE_DT;
  return Math.ceil(MAX_GHOST_RECORDING_SECONDS / safeStepDt);
}

/**
 * Decide whether a freshly-closed lap should overwrite the saved ghost.
 *
 * The "no ghost yet" branch is what catches returning players: anyone who
 * had a `bestLap` in localStorage from before ghost mode shipped will rarely
 * (if ever) PB on their first ghost-era lap, so without this rule they could
 * race indefinitely and never see a ghost. The first completed clean lap
 * seeds the ghost; from there only a real PB updates it.
 */
export function shouldSaveGhost(opts: {
  isPersonalBest: boolean;
  hasGhost: boolean;
  isAssisted: boolean;
}): boolean {
  if (opts.isAssisted) return false;
  if (opts.isPersonalBest) return true;
  return !opts.hasGhost;
}

export interface GhostProgressSample {
  progress: number;
  elapsedMs: number;
}

export function buildGhostProgressSamples(
  rec: GhostRecording,
  arcLengthForState: (state: GhostStartState) => number,
  totalLength: number,
): GhostProgressSample[] {
  const samples: GhostProgressSample[] = [{ progress: 0, elapsedMs: 0 }];
  const startArc = normalizeArc(arcLengthForState(rec.startState), totalLength);
  let prevArc = startArc;
  let wrapOffset = 0;

  for (let i = 0; i < rec.states.length; i++) {
    const arc = normalizeArc(arcLengthForState(rec.states[i]), totalLength);
    const delta = arc - prevArc;
    if (delta < -totalLength * 0.5) wrapOffset += totalLength;
    else if (delta > totalLength * 0.5) wrapOffset -= totalLength;
    prevArc = arc;

    samples.push({
      progress: Math.max(0, arc + wrapOffset - startArc),
      elapsedMs: (i + 1) * rec.stepDt * 1000,
    });
  }

  return samples;
}

export function computeGhostProgressDelta(
  liveElapsedMs: number | null,
  liveProgress: number | null,
  ghostSamples: GhostProgressSample[],
): number | null {
  if (liveElapsedMs === null || liveProgress === null) return null;
  const ghostElapsed = ghostElapsedAtProgress(ghostSamples, liveProgress);
  return ghostElapsed === null ? null : liveElapsedMs - ghostElapsed;
}

export function ghostElapsedAtProgress(
  samples: GhostProgressSample[],
  progress: number,
): number | null {
  if (samples.length === 0) return null;
  if (progress <= samples[0].progress) return samples[0].elapsedMs;
  for (let i = 1; i < samples.length; i++) {
    const prev = samples[i - 1];
    const next = samples[i];
    if (progress > next.progress) continue;
    const span = next.progress - prev.progress;
    if (span <= 1e-6) return next.elapsedMs;
    const t = (progress - prev.progress) / span;
    return prev.elapsedMs + (next.elapsedMs - prev.elapsedMs) * t;
  }
  return samples[samples.length - 1].elapsedMs;
}

export function buildGhostSectorTimes(
  rec: GhostRecording,
  samples: GhostProgressSample[],
  totalLength: number,
): [number, number, number] | null {
  if (totalLength <= 0) return null;
  const sector1End = ghostElapsedAtProgress(samples, totalLength / 3);
  const sector2End = ghostElapsedAtProgress(samples, (totalLength * 2) / 3);
  if (sector1End === null || sector2End === null) return null;
  const sector3End = rec.lapMs;
  const sectors: [number, number, number] = [
    sector1End,
    sector2End - sector1End,
    sector3End - sector2End,
  ];
  return sectors.every((sector) => Number.isFinite(sector) && sector >= 0)
    ? sectors
    : null;
}

function normalizeArc(arc: number, totalLength: number): number {
  if (totalLength <= 0) return arc;
  return ((arc % totalLength) + totalLength) % totalLength;
}

const STORAGE_PREFIX = 'tst:ghost:pb:';

export function ghostStorageKey(trackId: string, storageScope: string | null = null): string {
  return storageScope
    ? `${STORAGE_PREFIX}${trackId}:${storageScope}`
    : STORAGE_PREFIX + trackId;
}

export function saveGhost(rec: GhostRecording, storageScope: string | null = null): void {
  try {
    localStorage.setItem(ghostStorageKey(rec.trackId, storageScope), JSON.stringify(rec));
  } catch {
    // quota / disabled storage; ghost is non-essential.
  }
}

/**
 * Load a recording. Replay is keyframe-driven — {@link GhostShip.step}
 * reads `states[i]` and never re-simulates physics — so a recording stays
 * playable across PHYSICS_VERSION bumps as long as it carries per-step
 * height (schema v4). v3 recordings predate stored height and re-derive y
 * from the live ground sampler; we still gate those on physics version
 * because terrain retunes shipped alongside the early version stamps.
 */
export function loadGhost(
  trackId: string,
  currentPhysicsVersion: string,
  expectedStartPose: GhostStartPose | null = null,
  storageScope: string | null = null,
): GhostRecording | null {
  const scoped = loadGhostFromKey(ghostStorageKey(trackId, storageScope), trackId, currentPhysicsVersion, expectedStartPose);
  if (scoped || !storageScope?.startsWith('device:')) return scoped;
  const legacy = loadGhostFromKey(ghostStorageKey(trackId), trackId, currentPhysicsVersion, expectedStartPose);
  if (legacy) saveGhost(legacy, storageScope);
  return legacy;
}

function loadGhostFromKey(
  storageKey: string,
  trackId: string,
  currentPhysicsVersion: string,
  expectedStartPose: GhostStartPose | null,
): GhostRecording | null {
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return null;
    const data = JSON.parse(raw) as unknown;
    if (!isGhostRecording(data)) return null;
    if (data.trackId !== trackId) return null;
    if (data.schemaVersion < 4 && data.physicsVersion !== currentPhysicsVersion) return null;
    if (expectedStartPose && !startPoseMatches(data.startState, expectedStartPose)) return null;
    return data;
  } catch {
    return null;
  }
}

function startPoseMatches(actual: GhostStartPose, expected: GhostStartPose): boolean {
  const distance = Math.hypot(actual.x - expected.x, actual.z - expected.z);
  const headingDelta = Math.abs(Math.atan2(
    Math.sin(actual.heading - expected.heading),
    Math.cos(actual.heading - expected.heading),
  ));
  return distance <= 1.0 && headingDelta <= 0.15;
}

export function isGhostRecording(data: unknown): data is GhostRecording {
  if (!data || typeof data !== 'object') return false;
  const r = data as Partial<GhostRecording>;
  if (r.schemaVersion !== 3 && r.schemaVersion !== 4) return false;
  const requireY = r.schemaVersion === 4;
  if (typeof r.trackId !== 'string') return false;
  if (typeof r.lapMs !== 'number' || !Number.isFinite(r.lapMs)) return false;
  if (typeof r.physicsVersion !== 'string') return false;
  if (typeof r.stepDt !== 'number' || !Number.isFinite(r.stepDt) || r.stepDt <= 0) return false;
  if (!r.startState
    || !isFiniteNumber(r.startState.x)
    || !isFiniteNumber(r.startState.z)
    || !isFiniteNumber(r.startState.heading)
    || !isFiniteNumber(r.startState.velocityX)
    || !isFiniteNumber(r.startState.velocityZ)) return false;
  if (requireY && !isFiniteNumber(r.startState.y)) return false;
  if (!r.inputs) return false;
  const i = r.inputs;
  if (!Array.isArray(i.throttle) || !Array.isArray(i.brake)
    || !Array.isArray(i.steer) || !Array.isArray(i.onTrack)) return false;
  const n = i.throttle.length;
  if (n < 2) return false;
  if (i.brake.length !== n || i.steer.length !== n || i.onTrack.length !== n) return false;
  for (let idx = 0; idx < n; idx++) {
    if (!isFiniteNumber(i.throttle[idx])) return false;
    if (!isFiniteNumber(i.brake[idx])) return false;
    if (!isFiniteNumber(i.steer[idx])) return false;
    if (typeof i.onTrack[idx] !== 'boolean') return false;
  }
  if (!Array.isArray(r.states) || r.states.length !== n) return false;
  for (const s of r.states) {
    if (!s
      || !isFiniteNumber(s.x)
      || !isFiniteNumber(s.z)
      || !isFiniteNumber(s.heading)
      || !isFiniteNumber(s.velocityX)
      || !isFiniteNumber(s.velocityZ)) return false;
    if (requireY && !isFiniteNumber(s.y)) return false;
  }
  return true;
}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}
